import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Clock } from "lucide-react";
import { FadeUp, Overline } from "@/components/Reveal";

const SIZE = 600;
const N = 7;
const R_OUT = 268;
const R_IN = 186;

const starPath = () => {
  const pts = [];
  for (let i = 0; i < N * 2; i += 1) {
    const angle = -Math.PI / 2 + (i * Math.PI) / N;
    const r = i % 2 === 0 ? R_OUT : R_IN;
    pts.push([300 + r * Math.cos(angle), 300 + r * Math.sin(angle)]);
  }
  return `M ${pts.map(([x, y]) => `${x.toFixed(1)} ${y.toFixed(1)}`).join(" L ")} Z`;
};

const heptaPath = (radius) => {
  const pts = [];
  for (let i = 0; i < N; i += 1) {
    const angle = -Math.PI / 2 + Math.PI / N + (i * 2 * Math.PI) / N;
    pts.push([300 + radius * Math.cos(angle), 300 + radius * Math.sin(angle)]);
  }
  return `M ${pts.map(([x, y]) => `${x.toFixed(1)} ${y.toFixed(1)}`).join(" L ")} Z`;
};

export const POINTS = [
  {
    nr: "01",
    x: 300,
    y: 452,
    title: "Poarta a III-a",
    era: "1738",
    dur: "Start tur",
    img: "/assets/cetate-aerian-sepia.webp",
    text:
      "Intrarea monumentală a fortăreței și cea mai spectaculoasă dintre cele șapte porți. Aici începe traseul festivalului: arcada barocă devine, pentru două zile, portalul de intrare în RISE 27'.",
  },
  {
    nr: "02",
    x: 300,
    y: 308,
    title: "Platoul Central & Scena Mare",
    era: "Azi",
    dur: "15 min",
    img: "/assets/cetate-drona-apus.jpg",
    text:
      "Inima cetății, spațiul deschis dintre cele două catedrale. Aici se ridică scena principală a festivalului — sunet, lumini și mii de oameni între ziduri de 300 de ani.",
  },
  {
    nr: "03",
    x: 392,
    y: 268,
    title: "Catedrala Sfântul Mihail",
    era: "1247–1291",
    dur: "10 min",
    img: "/assets/cetate-aerian-sepia.webp",
    text:
      "Cea mai veche construcție a cetății și una dintre cele mai importante catedrale gotice din Transilvania. În interior se află mormintele lui Iancu de Hunedoara și ale principilor Transilvaniei.",
  },
  {
    nr: "04",
    x: 208,
    y: 262,
    title: "Palatul Principilor",
    era: "Sec. XVI",
    dur: "10 min",
    img: "/assets/cetate-ziduri-bastion.jpg",
    text:
      "Reședința principilor Transilvaniei, locul din care s-a condus țara timp de aproape un secol. Un labirint de curți interioare pe care tinerii îl explorează cu ghizi și actori în costume de epocă.",
  },
  {
    nr: "05",
    x: 243,
    y: 196,
    title: "Sala Unirii",
    era: "1 Decembrie 1918",
    dur: "20 min",
    img: "/assets/cetate-drona-apus.jpg",
    text:
      "Aici, cei 1.228 de delegați au votat Unirea Transilvaniei cu România. Punctul emoțional al traseului: proiecții, mărturii sonore și povestea zilei care a schimbat harta țării.",
  },
  {
    nr: "06",
    x: 300,
    y: 168,
    title: "Obeliscul Horea, Cloșca și Crișan",
    era: "1937",
    dur: "5 min",
    img: "/assets/cetate-ziduri-bastion.jpg",
    text:
      "Monumentul de 22 de metri dedicat conducătorilor răscoalei din 1784. Locul de unde pornește ruta de graffiti și poezie urbană — istoria spusă în limbajul generației noi.",
  },
  {
    nr: "07",
    x: 196,
    y: 476,
    title: "Bastionul Trinitarilor & Șanțurile Vauban",
    era: "1715–1738",
    dur: "25 min",
    img: "/assets/cetate-ziduri-bastion.jpg",
    text:
      "Traseul de sub ziduri, printre bastioane în formă de stea și șanțuri de apărare. Zona de ateliere și de scenă underground a festivalului, acolo unde cărămida veche amplifică basul.",
  },
];

const Hotspot = ({ point, active, onSelect }) => (
  <button
    type="button"
    data-testid={`cetate-hotspot-${point.nr}`}
    onClick={() => onSelect(point.nr)}
    aria-label={point.title}
    className="absolute -translate-x-1/2 -translate-y-1/2"
    style={{ left: `${(point.x / SIZE) * 100}%`, top: `${(point.y / SIZE) * 100}%` }}
  >
    <span
      className={`relative flex h-8 w-8 items-center justify-center rounded-full border font-mono text-[10px] font-bold transition-all duration-300 ${
        active
          ? "scale-125 border-neon bg-neon text-canvas shadow-[0_0_24px_rgba(204,255,0,0.6)]"
          : "border-neon/50 bg-canvas/80 text-neon hover:scale-110 hover:border-neon hover:bg-neon/20"
      }`}
    >
      {point.nr}
      {active && (
        <span className="absolute inset-0 animate-ping rounded-full border border-neon" />
      )}
    </span>
  </button>
);

const TurCetate = () => {
  const [activeNr, setActiveNr] = useState(POINTS[0].nr);
  const active = POINTS.find((p) => p.nr === activeNr);

  return (
    <section
      data-testid="cetate-tour-section"
      className="border-t border-white/10 bg-surface"
    >
      <div className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-12 lg:py-32">
        <FadeUp>
          <Overline>Tur interactiv</Overline>
          <h2 className="mt-4 max-w-2xl font-display text-3xl font-black uppercase leading-[1.02] tracking-tight text-white sm:text-4xl lg:text-5xl">
            Cetatea în <span className="text-neon">7 puncte</span>
          </h2>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-neutral-400">
            Apasă pe orice punct de pe hartă și descoperă povestea din spatele
            lui. Același traseu îl vei parcurge la RISE 27', cu ghizi și actori.
          </p>
        </FadeUp>

        <div className="mt-14 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
          <FadeUp className="lg:col-span-7">
            <div
              data-testid="cetate-tour-map"
              className="relative aspect-square w-full overflow-hidden rounded-3xl border border-white/10 bg-canvas"
            >
              <svg
                viewBox={`0 0 ${SIZE} ${SIZE}`}
                className="pointer-events-none absolute inset-0 h-full w-full"
                aria-hidden="true"
              >
                <defs>
                  <pattern id="tur-grid" width="30" height="30" patternUnits="userSpaceOnUse">
                    <path d="M 30 0 L 0 0 0 30" fill="none" stroke="#ffffff" strokeWidth="0.4" opacity="0.06" />
                  </pattern>
                </defs>
                <rect width={SIZE} height={SIZE} fill="url(#tur-grid)" />
                <path d={starPath()} fill="#CCFF00" fillOpacity="0.04" stroke="#CCFF00" strokeWidth="1.6" strokeOpacity="0.55" />
                <path d={heptaPath(R_IN - 22)} fill="none" stroke="#FF4D00" strokeWidth="1.2" strokeOpacity="0.45" />
                <path d={heptaPath(R_IN - 44)} fill="none" stroke="#ffffff" strokeWidth="0.8" strokeOpacity="0.14" />
                <line x1="300" y1="452" x2="300" y2="168" stroke="#CCFF00" strokeWidth="1" strokeOpacity="0.3" strokeDasharray="6 8" />
                <line x1="208" y1="262" x2="392" y2="268" stroke="#ffffff" strokeWidth="0.8" strokeOpacity="0.12" strokeDasharray="4 8" />
                <circle cx="300" cy="300" r="4" fill="#CCFF00" fillOpacity="0.8" />
                <text x="300" y="34" textAnchor="middle" className="font-mono" fill="#6B7280" fontSize="13" letterSpacing="6">
                  NORD
                </text>
              </svg>

              {POINTS.map((p) => (
                <Hotspot key={p.nr} point={p} active={p.nr === activeNr} onSelect={setActiveNr} />
              ))}

              <span className="absolute bottom-4 left-4 font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                Cetatea Alba Carolina · plan schematic Vauban
              </span>
            </div>
          </FadeUp>

          <div className="lg:col-span-5">
            <AnimatePresence mode="wait">
              <motion.div
                key={active.nr}
                data-testid="cetate-tour-detail-panel"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="overflow-hidden rounded-3xl border border-white/10 bg-carddark"
              >
                <img
                  src={active.img}
                  alt={active.title}
                  loading="lazy"
                  className="aspect-[16/10] w-full object-cover"
                />
                <div className="p-7 sm:p-8">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="rounded-full bg-neon px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-canvas">
                      Punct {active.nr}
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-flame">
                      {active.era}
                    </span>
                    <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                      <Clock className="h-3 w-3" /> {active.dur}
                    </span>
                  </div>
                  <h3 className="mt-5 font-display text-xl font-black uppercase leading-tight tracking-tight text-white sm:text-2xl">
                    {active.title}
                  </h3>
                  <p className="mt-4 text-sm leading-relaxed text-neutral-300">
                    {active.text}
                  </p>
                </div>
              </motion.div>
            </AnimatePresence>

            <div className="mt-4 flex flex-wrap gap-2">
              {POINTS.map((p) => (
                <button
                  key={p.nr}
                  type="button"
                  data-testid={`cetate-tour-chip-${p.nr}`}
                  onClick={() => setActiveNr(p.nr)}
                  className={`rounded-full px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.18em] transition-colors duration-300 ${
                    p.nr === activeNr
                      ? "bg-neon text-canvas"
                      : "border border-white/15 text-neutral-400 hover:border-neon hover:text-neon"
                  }`}
                >
                  {p.nr}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div data-testid="cetate-tour-list" className="mt-20 space-y-px bg-white/10">
          {POINTS.map((p, i) => (
            <FadeUp key={`list-${p.nr}`} delay={i * 0.05} className="bg-surface">
              <div
                data-testid={`cetate-tour-list-item-${p.nr}`}
                className="group grid grid-cols-1 gap-4 py-8 transition-colors duration-500 hover:bg-canvas/50 md:grid-cols-12 md:gap-8"
              >
                <span className="font-mono text-sm font-bold text-flame md:col-span-1">
                  {p.nr}
                </span>
                <div className="md:col-span-4">
                  <h4 className="font-display text-lg font-black uppercase tracking-tight text-white transition-colors duration-300 group-hover:text-neon">
                    {p.title}
                  </h4>
                  <p className="mt-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                    <MapPin className="h-3 w-3 text-neon" /> {p.era} · {p.dur}
                  </p>
                </div>
                <p className="text-sm leading-relaxed text-neutral-400 md:col-span-7">
                  {p.text}
                </p>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TurCetate;
